import config from '../../../config';

export function parseActionItems(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

export function formatDateOnly(value) {
    if (!value) return '';
    return String(value).slice(0, 10);
}

/** Target date is stored on the CAR as dueDate (user-entered). */
export function computeCarTargetDate(_actionItems, fallbackDue) {
    return formatDateOnly(fallbackDue);
}

export function computeCarDisplayStatus(status, _dateClosed, targetDate) {
    const normalizedStatus = String(status || '').trim();
    if (normalizedStatus === 'Closed') return 'Closed';
    const today = new Date().toISOString().slice(0, 10);
    if (targetDate && targetDate < today) return 'Overdue';
    if (normalizedStatus === 'In Progress') return 'In Progress';
    return 'Open';
}

export const CAR_STATUS_STYLES = {
    Closed: { backgroundColor: '#d4edda', color: '#155724' },
    'In Progress': { backgroundColor: '#cce5ff', color: '#004085' },
    Overdue: { backgroundColor: '#f8d7da', color: '#721c24' },
    Open: { backgroundColor: '#fff3cd', color: '#856404' },
};

export const CAR_ORIGIN_OPTIONS = [
    'Customer Complaint',
    'Internal Audit',
    'External Audit',
    'Scrap/Defect',
];

export const CAR_ROW_STATUS_OPTIONS = ['Open', 'In Progress', 'Overdue', 'Closed'];

export const CAR_EFFECTIVENESS_METHODS = [
    'Re-audit Process',
    'Review Yield Data',
    'Operator Interview',
];

export const DEFAULT_ROOT_CAUSE = {
    problemStatement: '',
    why1: '',
    why2: '',
    why3: '',
    why4: '',
    why5: '',
    finalStatement: '',
};

export const DEFAULT_EFFECTIVENESS = {
    verificationMethods: [],
    evidenceChecked: '',
    evidencePhotos: [],
    verifiedBy: '',
    verificationDate: '',
};

export function emptyContainmentRow() {
    return { actionTaken: '', responsiblePerson: '', dateCompleted: '', status: 'Open' };
}

export function emptyCorrectivePlanRow() {
    return {
        actionDescription: '',
        systemDocumentUpdated: '',
        owner: '',
        targetDate: '',
        completionDate: '',
    };
}

export function parseJsonArray(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

export function parseJsonObject(raw, fallback) {
    if (!raw) return { ...fallback };
    if (typeof raw === 'object' && !Array.isArray(raw)) return { ...fallback, ...raw };
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? { ...fallback, ...parsed }
            : { ...fallback };
    } catch (e) {
        return { ...fallback };
    }
}

/** Public URL for files stored under the API server's /img static route. */
export function imageUrlFromPath(imgPath) {
    if (!imgPath) return '';
    if (/^https?:\/\//i.test(imgPath)) return imgPath;

    let rel = String(imgPath).trim().replace(/\\/g, '/');
    if (rel.startsWith('/var/') || /^[a-z]:/i.test(rel)) {
        const base = rel.split('/').pop();
        rel = base ? `img/${base}` : '';
    } else if (rel.startsWith('/img/')) {
        rel = rel.slice(1);
    } else {
        rel = rel.replace(/^\/+/, '');
    }
    if (!rel) return '';

    const filePart = rel.startsWith('img/') ? rel.slice('img/'.length) : rel;
    const encodedFile = filePart.split('/').map((seg) => encodeURIComponent(seg)).join('/');

    const apiUrl = String(config.apiUrl || '').replace(/\/+$/, '');
    const publicBase = config.imagePublicBaseUrl
        ? String(config.imagePublicBaseUrl).replace(/\/+$/, '')
        : /\/api$/i.test(apiUrl)
            ? apiUrl.replace(/\/api$/i, '')
            : apiUrl;

    // Test/staging: static files live under dist_test/img at site /img/ (nginx alias)
    if (config.imagePublicBaseUrl) {
        return `${publicBase}/img/${encodedFile}`;
    }
    if (/\/api$/i.test(apiUrl)) {
        return `${apiUrl}/img/${encodedFile}`;
    }
    return rel.startsWith('img/') ? `${apiUrl}/${rel.split('/').map(encodeURIComponent).join('/')}` : `${apiUrl}/img/${encodedFile}`;
}

/** Normalize upload / DB photo rows so imgPath and fileType work with previews and print. */
export function normalizeReportMediaItem(item) {
    if (!item || typeof item !== 'object') return null;
    let imgPath = item.imgPath || item.url || item.path || '';
    if (imgPath && !/^https?:\/\//i.test(imgPath)) {
        const pathStr = String(imgPath).replace(/\\/g, '/');
        if (pathStr.startsWith('/var/') || /^[a-z]:/i.test(pathStr)) {
            const base = pathStr.split('/').pop();
            imgPath = base ? `img/${base}` : '';
        } else if (pathStr.startsWith('/img/')) {
            imgPath = pathStr.slice(1);
        } else if (!pathStr.startsWith('img/') && item.filename) {
            imgPath = `img/${item.filename}`;
        } else {
            imgPath = pathStr.replace(/^\/+/, '');
        }
    }
    const filename = item.filename || (imgPath ? String(imgPath).split('/').pop() : '');
    const normalized = {
        ...item,
        filename,
        imgPath: imgPath || (filename ? `img/${filename}` : ''),
    };
    if (!normalized.fileType && normalized.imgPath) {
        const probe = String(
            normalized.originalname || normalized.filename || normalized.imgPath
        ).toLowerCase();
        if (/\.(jpe?g|png|gif|webp|bmp)$/i.test(probe.split(/[/\\]/).pop())) {
            normalized.fileType = 'image';
        }
    }
    return normalized.imgPath || normalized.filename ? normalized : null;
}

export function parseReportMediaArray(raw) {
    return parseJsonArray(raw)
        .map((item) => normalizeReportMediaItem(item))
        .filter(Boolean);
}

export function mapUploadedImageFiles(files) {
    return (files || []).map((item) => normalizeReportMediaItem({
        filename: item.filename,
        originalname: item.originalname,
        imgPath: item.filename ? `img/${item.filename}` : '',
        fileType: 'image',
    })).filter(Boolean);
}
